import React, { useEffect, useMemo, useRef, useState } from "react";
import "./OnboardingBilling.scss";
import { loadStripe, Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { FormattedMessage } from "react-intl";
import { Socket } from "socket.io-client";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import { LoadingResults } from "@components/shared/LoadingResults/LoadingResults";
import { CreditCardBrandIcon } from "@components/shared/CreditCardBrandIcon/CreditCardBrandIcon";
import EndpointsEnum from "@models/EndpointsEnum";
import IErrorDTO from "@models/DTO/IErrorDTO";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import IStripeCreateSubscriptionSetupIntentDTO from "@models/DTO/IStripeCreateSubscriptionSetupIntentDTO";
import IStripeOnCreateSubscriptionSetupIntentDTO from "@models/DTO/IStripeOnCreateSubscriptionSetupIntentDTO";
import IStripeCompleteSubscriptionSetupDTO from "@models/DTO/IStripeCompleteSubscriptionSetupDTO";
import IStripeOnCompleteSubscriptionSetupDTO from "@models/DTO/IStripeOnCompleteSubscriptionSetupDTO";
import IStripeSubscriptionPaymentMethod from "@models/Stripe/IStripeSubscriptionPaymentMethod";
import IStripeSubscriptionSummary from "@models/Stripe/IStripeSubscriptionSummary";
import ISupplierModel from "@models/ISupplierModel";
import IUserUI from "@models/IUserUI";
import UserService from "@services/UserService";

type Props = {
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    theme: "LIGHT" | "DARK";
    /** Where Stripe sends the browser back after a redirect-based confirmation (lands on this slide). */
    returnUrl: string;
    /** SetupIntent id from that redirect's query string, when we just came back from one. */
    redirectedSetupIntentId?: string;
    /** Whether the wizard may move past this step, plus the method on file for the review slide. */
    onReadyChange: (ready: boolean, paymentMethod: IStripeSubscriptionPaymentMethod | null) => void;
    /** Hosts with their own heading (the login billing modal) hide the step's "let's set up billing" title. */
    hideTitle?: boolean;
};

/**
 * Whether the onboarding billing step is shown: a business that is invoiced by hand (stripe_no_auto_pay) is not
 * asked for a payment method. A build without the Stripe publishable key still shows the step, with a notice
 * that payment setup is not configured, rather than silently dropping it.
 */
export const isOnboardingBillingEnabled = (supplierModel: ISupplierModel): boolean => {
    return !supplierModel.stripe_no_auto_pay;
};

/** The Stripe publishable key is baked into the build from STRIPE_KEY (rsbuild.config.ts). */
export const isStripeConfigured = (): boolean => {
    return !!process.env.STRIPE_KEY;
};

export type SubscriptionPaymentFormProps = {
    returnUrl: string;
    saving: boolean;
    setSaving: (saving: boolean) => void;
    onConfirmed: (setupIntentId: string) => void;
    onCancel?: () => void;
    /** Translation key for the cancel button; defaults to keeping the current method. */
    cancelTitle?: string;
};

/**
 * Inner form: must live under <Elements> to reach the Stripe instance and the mounted Payment Element.
 * confirmSetup saves the card / bank account against the SetupIntent; nothing is charged here.
 */
export const SubscriptionPaymentForm: React.FC<SubscriptionPaymentFormProps> = (props) => {
    const stripe = useStripe();
    const elements = useElements();

    const [formError, setFormError] = useState<string | null>(null);

    const submit = async () => {
        if (!stripe || !elements || props.saving) {
            return;
        }

        props.setSaving(true);
        setFormError(null);

        const { error, setupIntent } = await stripe.confirmSetup({
            elements,
            // Cards and instantly-verified bank accounts resolve in place; only methods that truly need a
            // redirect leave the page, and come back to returnUrl with the SetupIntent in the query string.
            redirect: "if_required",
            confirmParams: { return_url: props.returnUrl },
        });

        if (error) {
            props.setSaving(false);
            setFormError(error.message || null);
            return;
        }

        if (setupIntent) {
            props.onConfirmed(setupIntent.id);
        } else {
            props.setSaving(false);
        }
    };

    return (
        <div className={"SubscriptionPaymentForm"}>
            <PaymentElement options={{ layout: "tabs" }} />

            {formError ? (
                <div className={"text-danger mt-2"} role={"alert"}>
                    {formError}
                </div>
            ) : null}

            <div className={"mt-3 d-flex"}>
                <OwButton
                    title={"save_payment_method"}
                    fn={submit}
                    active={props.saving}
                    disableOnActive={true}
                    disabled={!stripe || !elements}
                    testId={"onboarding-billing-save"}
                />
                {props.onCancel ? (
                    <OwButton
                        className={"btn-secondary ms-3"}
                        title={props.cancelTitle || "keep_current_payment_method"}
                        fn={props.onCancel}
                        disabled={props.saving}
                    />
                ) : null}
            </div>
        </div>
    );
};

export const PaymentMethodSummary: React.FC<{ paymentMethod: IStripeSubscriptionPaymentMethod }> = ({ paymentMethod }) => {
    const isBank = paymentMethod.type === "us_bank_account";

    return (
        <span className={"PaymentMethodSummary"} data-testid={"payment-method-summary"}>
            {isBank ? <i className={"fad fa-university me-2"} /> : <CreditCardBrandIcon brand={paymentMethod.brand || ""} />}
            <span className={isBank ? "" : "ms-2"}>
                {isBank ? paymentMethod.bank_name || <OwLabel title={"bank_account"} /> : (paymentMethod.brand || "").toUpperCase()}
            </span>
            <span className={"ms-2"}>
                <OwLabel title={"ending_in"} /> {paymentMethod.last4}
            </span>
            {!isBank && paymentMethod.exp_month && paymentMethod.exp_year ? (
                <span className={"ms-2 text-muted"}>
                    ({paymentMethod.exp_month}/{paymentMethod.exp_year})
                </span>
            ) : null}
        </span>
    );
};

const formatMoney = (amount: number | null, currency: string): string => {
    if (amount === null) {
        return "";
    }

    return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format(amount);
};

const formatUnixDate = (unix: number | null): string => {
    return unix ? UserService.parseDate(new Date(unix * 1000).toISOString()) : "";
};

/**
 * What they are signing up for, so the admin sees the plan the payment method is going on: each line, the
 * per-period total, and the first / next charge with its date. Nothing here is editable; the plan is
 * whatever Orderwerks set up for them in Stripe.
 */
export const SubscriptionSummary: React.FC<{ subscription: IStripeSubscriptionSummary }> = ({ subscription }) => {
    const intervalKey = subscription.interval ? `billing_interval_${subscription.interval}` : null;

    const period = intervalKey ? (
        subscription.interval_count > 1 ? (
            <FormattedMessage
                id={`billing_every_n_${subscription.interval}s`}
                defaultMessage={`_billing_every_n_${subscription.interval}s_`}
                values={{ count: subscription.interval_count }}
            />
        ) : (
            <OwLabel title={intervalKey} />
        )
    ) : null;

    const nextCharge = subscription.next_invoice_total ?? subscription.period_total;
    const nextChargeDate = formatUnixDate(subscription.next_charge_date);
    const isTrialing = !!subscription.trial_end && subscription.trial_end * 1000 > Date.now();

    return (
        <div className={"SubscriptionSummary"} data-testid={"subscription-summary"}>
            <div>
                <OwLabel title={"your_subscription"} isBold={true} />
            </div>

            <table className={"subscriptionTable"}>
                <tbody>
                    {subscription.items.map((item, index) => (
                        <tr key={index}>
                            <td>
                                {item.description}
                                {item.quantity > 1 ? <span className={"periodNote ms-2"}>x {item.quantity}</span> : null}
                            </td>
                            <td className={"amount"}>
                                {item.amount === null ? <OwLabel title={"usage_based"} /> : formatMoney(item.amount, subscription.currency)}
                            </td>
                        </tr>
                    ))}
                    {subscription.period_total !== null ? (
                        <tr className={"totalRow"}>
                            <td>
                                <OwLabel title={"total"} /> {period ? <span className={"periodNote"}>({period})</span> : null}
                            </td>
                            <td className={"amount"}>{formatMoney(subscription.period_total, subscription.currency)}</td>
                        </tr>
                    ) : null}
                </tbody>
            </table>

            {nextChargeDate && nextCharge !== null ? (
                <div className={"nextCharge"} data-testid={"subscription-next-charge"}>
                    <FormattedMessage
                        id={isTrialing ? "billing_first_charge" : "billing_next_charge"}
                        defaultMessage={isTrialing ? "_billing_first_charge_" : "_billing_next_charge_"}
                        values={{ amount: formatMoney(nextCharge, subscription.currency), date: nextChargeDate }}
                    />
                </div>
            ) : null}
        </div>
    );
};

/**
 * Onboarding billing step. Asks the business admin for a credit card or US bank account (ACH debit) for the
 * Orderwerks subscription. The API creates a SetupIntent, the Payment Element collects the details PCI-safely,
 * and once confirmed the API makes it the default payment method and creates the subscription with the
 * agreement's trial, so Stripe charges it automatically when the trial ends and monthly after that.
 */
export const OnboardingBilling: React.FC<Props> = (props) => {
    const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
        return process.env.STRIPE_KEY ? loadStripe(process.env.STRIPE_KEY) : null;
    }, []);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | undefined>();
    const [paymentMethodOnFile, setPaymentMethodOnFile] = useState<IStripeSubscriptionPaymentMethod | null>(null);
    const [isRequired, setIsRequired] = useState(true);
    const [trialEndDate, setTrialEndDate] = useState<string | null>(null);
    const [subscription, setSubscription] = useState<IStripeSubscriptionSummary | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [verificationUrl, setVerificationUrl] = useState<string | null>(null);
    const [hasError, setHasError] = useState(false);

    // A redirect return is handled once; a re-render must not re-submit the same SetupIntent.
    const handledRedirectRef = useRef(false);

    const basePayload = () => ({
        userGuid: props.userModel.guid,
        userToken: props.userModel.token,
        supplierGuid: props.supplierModel.guid,
    });

    const createSetupIntent = () => {
        setLoading(true);
        setHasError(false);
        setClientSecret(undefined);

        const payload: IStripeCreateSubscriptionSetupIntentDTO = basePayload();

        props.socket.emit(EndpointsEnum.StripeCreateSubscriptionSetupIntent, payload);
    };

    const completeSetup = (setupIntentId: string) => {
        setSaving(true);

        const payload: IStripeCompleteSubscriptionSetupDTO = {
            ...basePayload(),
            setupIntentId,
        };

        props.socket.emit(EndpointsEnum.StripeCompleteSubscriptionSetup, payload);
    };

    const onCreateSetupIntent = (data: IStripeOnCreateSubscriptionSetupIntentDTO) => {
        setClientSecret(data.client_secret);
        setPaymentMethodOnFile(data.paymentMethodOnFile);
        setIsRequired(data.isRequired);
        setTrialEndDate(data.trialEndDate);
        setSubscription(data.subscription);
        setShowForm(!data.paymentMethodOnFile);
        setLoading(false);

        props.onReadyChange(!!data.paymentMethodOnFile || !data.isRequired, data.paymentMethodOnFile);
    };

    const onCompleteSetup = (data: IStripeOnCompleteSubscriptionSetupDTO) => {
        setSaving(false);
        setLoading(false);
        setPaymentMethodOnFile(data.paymentMethod);
        setShowForm(false);
        setVerificationUrl(data.status === "requires_action" ? data.verificationUrl : null);

        if (data.subscription) {
            setSubscription(data.subscription);
        }

        const notification: INotification = {
            message: data.status === "requires_action" ? "bank_account_verification_pending" : "payment_method_saved",
            level: data.status === "requires_action" ? NotificationLevelEnum.warning : NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);

        // Either way the method is saved on the Stripe customer; a pending bank verification finishes on its
        // own (the API wires the subscription up from Stripe's webhook), so the wizard can move on.
        props.onReadyChange(true, data.paymentMethod);
    };

    const notify = (data: IErrorDTO) => {
        // Notify carries every API error; only react to this step's own.
        if (
            data?.message === "ERROR_STRIPE_CREATE_SUBSCRIPTION_SETUP_INTENT" ||
            data?.message === "ERROR_STRIPE_COMPLETE_SUBSCRIPTION_SETUP"
        ) {
            setLoading(false);
            setSaving(false);
            setHasError(true);
        }
    };

    useEffect(() => {
        if (!isStripeConfigured()) {
            // Nothing can be collected without the publishable key; say so and do not hold the wizard up.
            setLoading(false);
            props.onReadyChange(true, null);
            return;
        }

        props.socket.on(EndpointsEnum.StripeOnCreateSubscriptionSetupIntent, onCreateSetupIntent);
        props.socket.on(EndpointsEnum.StripeOnCompleteSubscriptionSetup, onCompleteSetup);
        props.socket.on(EndpointsEnum.Notify, notify);

        if (props.redirectedSetupIntentId && !handledRedirectRef.current) {
            handledRedirectRef.current = true;
            completeSetup(props.redirectedSetupIntentId);
        } else {
            createSetupIntent();
        }

        return () => {
            props.socket.removeListener(EndpointsEnum.StripeOnCreateSubscriptionSetupIntent, onCreateSetupIntent);
            props.socket.removeListener(EndpointsEnum.StripeOnCompleteSubscriptionSetup, onCompleteSetup);
            props.socket.removeListener(EndpointsEnum.Notify, notify);
        };
    }, []);

    const changePaymentMethod = () => {
        setShowForm(true);

        // A confirmed SetupIntent cannot be reused; get a fresh one for the new details.
        if (!clientSecret) {
            createSetupIntent();
        }
    };

    const elementsOptions: StripeElementsOptions | undefined = clientSecret
        ? {
              clientSecret,
              appearance: { theme: props.theme === "DARK" ? "night" : "stripe" },
          }
        : undefined;

    const trialNotice =
        trialEndDate && !subscription ? (
            <div className={"trialNotice mt-3"} data-testid={"onboarding-billing-trial"}>
                <i className={"fad fa-info-circle me-2"} />
                <FormattedMessage
                    id={"billing_no_charge_until_trial_ends"}
                    defaultMessage={"_billing_no_charge_until_trial_ends_"}
                    values={{ date: UserService.parseDate(trialEndDate) }}
                />
            </div>
        ) : null;

    const title = props.hideTitle ? null : <OwLabel title={"billing_step_title"} />;

    if (!isStripeConfigured()) {
        return (
            <div className={"OnboardingBilling"}>
                {title}

                <div className={"mt-3 alert alert-warning"} role={"alert"} data-testid={"onboarding-billing-unconfigured"}>
                    <i className={"fad fa-exclamation-triangle me-2"} />
                    <OwLabel title={"billing_step_not_configured"} />
                </div>
            </div>
        );
    }

    return (
        <div className={"OnboardingBilling"}>
            {title}

            <div className={"mt-3"}>
                <OwLabel title={"billing_step_intro"} />
            </div>

            {!isRequired ? (
                <div className={"mt-2 text-muted"}>
                    <OwLabel title={"billing_step_optional_notice"} />
                </div>
            ) : null}

            {subscription ? (
                <div className={"mt-3"}>
                    <SubscriptionSummary subscription={subscription} />
                </div>
            ) : !loading ? (
                <div className={"mt-3 text-muted"} data-testid={"subscription-pending"}>
                    <OwLabel title={"subscription_details_pending"} />
                </div>
            ) : null}

            {trialNotice}

            {paymentMethodOnFile && !showForm ? (
                <div className={"onFile mt-3"}>
                    <div>
                        <OwLabel title={"payment_method_on_file"} isBold={true} postText={":"} />
                    </div>
                    <div className={"mt-2"}>
                        <PaymentMethodSummary paymentMethod={paymentMethodOnFile} />
                    </div>

                    {verificationUrl ? (
                        <div className={"mt-2"}>
                            <div className={"text-warning"}>
                                <OwLabel title={"bank_account_verification_pending"} />
                            </div>
                            <a
                                href={verificationUrl}
                                target={"_blank"}
                                rel={"noreferrer"}
                                className={"btn btn-sm btn-outline-primary mt-2"}
                            >
                                <OwLabel title={"verify_bank_account"} />
                            </a>
                        </div>
                    ) : null}

                    <div className={"mt-3"}>
                        <OwButton className={"btn-secondary"} title={"change_payment_method"} fn={changePaymentMethod} disabled={saving} />
                    </div>
                </div>
            ) : null}

            {showForm ? (
                <div className={"mt-3"}>
                    {hasError ? (
                        <div className={"text-danger mb-2"} role={"alert"}>
                            <OwLabel title={"error_saving_payment_method"} />
                            <div className={"mt-2"}>
                                <OwButton className={"btn-secondary"} title={"retry"} fn={createSetupIntent} />
                            </div>
                        </div>
                    ) : null}

                    {loading || !stripePromise || !elementsOptions ? (
                        !hasError ? (
                            <LoadingResults />
                        ) : null
                    ) : (
                        <Elements key={clientSecret} stripe={stripePromise} options={elementsOptions}>
                            <SubscriptionPaymentForm
                                returnUrl={props.returnUrl}
                                saving={saving}
                                setSaving={setSaving}
                                onConfirmed={completeSetup}
                                onCancel={paymentMethodOnFile ? () => setShowForm(false) : undefined}
                            />
                        </Elements>
                    )}
                </div>
            ) : null}

            {saving && !showForm ? (
                <div className={"mt-3"}>
                    <LoadingResults />
                </div>
            ) : null}

            {isRequired && !paymentMethodOnFile ? (
                <div className={"mt-3 text-muted small"}>
                    <OwLabel title={"payment_method_required_to_continue"} />
                </div>
            ) : null}
        </div>
    );
};
